import {
  animate,
  keyframes,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, merge, Observable, of, throwError } from 'rxjs';
import { catchError, mapTo } from 'rxjs/operators';
import { PublicService } from 'src/app/api/services';
import { editionPrefix } from './app.component';
import { AuthService } from './auth/auth.service';
import {
  getPinWithEdition,
  getShortPin,
  PIN_LEGACY_REG,
  PIN_NO_HYPHEN_REG,
} from './utility/app.utils';

import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'qa-pin-input',
  templateUrl: './pin-input.component.html',
  styleUrls: ['./pin-input.component.scss'],
  animations: [
    trigger('invalidPinAnimation', [
      transition('false => true', [
        animate(
          '800ms ease-out',
          keyframes([
            style({ transform: 'translateX(-32px)' }),
            style({ transform: 'translateX(32px)' }),
            style({ transform: 'translateX(-24px)' }),
            style({ transform: 'translateX(24px)' }),
            style({ transform: 'translateX(-16px)' }),
            style({ transform: 'translateX(16px)' }),
            style({ transform: 'translateX(0)' }),
          ])
        ),
      ]),
    ]),
  ],
  imports: [TranslateModule],
})
export class PinInputComponent implements AfterViewInit {
  @ViewChildren('input')
  inputs: QueryList<ElementRef<HTMLInputElement>>;

  allowedCharacters = /[a-zA-Z0-9]/;
  invalidPinMessage = '';

  editionPrefix = editionPrefix();

  constructor(
    private router: Router,
    private api: PublicService,
    public auth: AuthService
  ) {}

  ngAfterViewInit() {
    const pasteEvents = this.inputs.map((input) => {
      const el = input.nativeElement;
      return fromEvent<ClipboardEvent>(el, 'paste');
    });
    merge(...pasteEvents).subscribe((event) => {
      event.preventDefault();
      let pastedText =
        event && event?.clipboardData
          ? event.clipboardData.getData('text/plain')
          : '';
      if (this.auth.isLoggedIn()) {
        /** This if condition will prevent legacy pins from getting trimmed for logged in users and
         *  will continue the further processing as legacy pins.
         *  */
        if (!PIN_LEGACY_REG.test(pastedText)) {
          const pastedPin = pastedText.split('-');
          if (pastedPin?.length && pastedPin[0] === this.editionPrefix) {
            pastedText = pastedText.substring(2);
          } else {
            this.invalidPinMessage = 'findContent.invalidEdition';
            return;
          }
        }
      } else {
        pastedText = `${pastedText[0]}${pastedText.substring(2)}`;
      }
      this.inputs.forEach((input, index) => {
        if (pastedText) {
          input.nativeElement.value = pastedText[index];
        }
      });
      if (pastedText && pastedText.length >= 6) {
        this.submit();
      }
    });

    this.auth.resetApiUrl();
  }

  keyPress(event: KeyboardEvent) {
    if (!this.allowedCharacters.test(event.key)) {
      event.preventDefault();
    }
  }

  keyUp(index: number, event: KeyboardEvent) {
    if (!this.allowedCharacters.test(event.key)) {
      return;
    }
    let nextIndex: number;
    if (event.key === 'Backspace') {
      nextIndex = index - 1;
      if (nextIndex < 0) {
        return;
      }
    } else {
      nextIndex = index + 1;
      const input = this.inputs.toArray()[index].nativeElement;
      if (!input.value) {
        return;
      }
      if (nextIndex > 5 && this.auth.isLoggedIn()) {
        this.submit();
        return;
      }
      if (nextIndex > 6 && !this.auth.isLoggedIn()) {
        this.submit();
        return;
      }
    }
    const nextInput = this.inputs.toArray()[nextIndex].nativeElement;
    this.invalidPinMessage = '';
    nextInput.focus();
    nextInput.select();
  }

  async redirectToPin() {
    var pin = this.inputs.reduce(
      (all, input) => all + input.nativeElement.value,
      ''
    );
    if (
      (!PIN_NO_HYPHEN_REG.test(pin) && !PIN_LEGACY_REG.test(pin)) ||
      (PIN_LEGACY_REG.test(pin) && !this.auth.isLoggedIn())
    ) {
      throw new Error('Invalid PIN: Invalid format');
    }

    this.auth.setApiUrl(pin);

    pin = getShortPin(pin);
    const checkPin = pin[0].toLowerCase();
    const possibleEditions = ['s', 'b', 'u', 'd'];
    if (!possibleEditions.includes(checkPin) && !this.auth.isLoggedIn()) {
      throw new Error('Invalid PIN: Unsupported first letter');
    }

    let fullPin = '';

    if (!this.auth.isLoggedIn()) {
      fullPin = pin.substring(0, 1) + '-' + pin.substring(1);
      pin = pin.slice(1);
    } else {
      fullPin = getPinWithEdition(pin);
    }

    const firstChar = pin[0].toLowerCase();
    let getEntity$: Observable<any>;
    switch (firstChar) {
      case 'c':
        getEntity$ = this.api.getCourseMeta({ pin });
        break;
      case 'b':
        getEntity$ = this.api.getBookMeta({ pin });
        break;
      case 'e':
        getEntity$ = this.api.getPublicExamByPin({ pin });
        break;
      case 'l':
        getEntity$ = this.api.getPublicLiveEventByPin({ pin });
        break;
      default:
        throw new Error('Invalid PIN: Unsupported first letter');
    }
    const entityExists = await getEntity$
      .pipe(
        mapTo(true),
        catchError((error) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 403) {
              return of(true);
            } else {
              return of(false);
            }
          }
          return throwError(error);
        })
      )
      .toPromise();
    if (!entityExists) throw new Error("Entity doesn't exist");
    const validChars = ['c', 'b', 'e', 'l'];
    if (validChars.includes(firstChar)) {
      this.router.navigateByUrl(`/${fullPin}`);
    } else {
      throw new Error('Invalid PIN: Unsupported first letter');
    }
  }

  async submit() {
    this.inputs.forEach((input) => {
      input.nativeElement.disabled = true;
    });
    try {
      await this.redirectToPin();
    } catch (error) {
      console.error(error);
      this.invalidPinMessage = 'findContent.invalidPin';
      this.inputs.forEach((input) => {
        input.nativeElement.value = '';
        input.nativeElement.disabled = false;
      });
      this.inputs.first.nativeElement.focus();
    }
  }
}
